import { Control, Controller, FieldValues, Path, RegisterOptions } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FloatLabel, Password } from "../../Common/Imports";
import { ValidatePassword } from '../../Common/Utility/ValidatePassword';

interface PasswordFieldProps<T extends FieldValues> {
    control: Control<T>;
    name: Path<T>;
    label: string;
    validationRules?: Omit<RegisterOptions<T, Path<T>>, "setValueAs" | "disabled" | "valueAsNumber" | "valueAsDate">;
}

export const PasswordField = <T extends FieldValues>({ control, name, validationRules, label }: PasswordFieldProps<T>) => {
    const { t } = useTranslation();

    const getStrengthLabel = (value: string): string => {
        if (/^.{0,5}$/.test(value)) {
            return t("Common.Password.Need6Chars");
        }
        if (/^(?!.*[A-Z]).*$/.test(value)) {
            return t("Common.Password.Need1Uppercase");
        }
        if (/^(?!.*[a-z]).*$/.test(value)) {
            return t("Common.Password.Need1Lowercase");
        }
        if (/^(?!.*\d).*$/.test(value)) {
            return t("Common.Password.Need1Digit");
        }
        if (/^(?!.*[\W_]).*$/.test(value)) {
            return t("Common.Password.Need1NotAlphanumeric");
        }
        return t("Common.Password.Ok");
    };

    return (
        <Controller
            name={name}
            control={control}
            rules={{
                required: `${label} ${t("Common.Form.Required")}`,
                validate: (value) =>
                    (value && ValidatePassword(value)) ||
                    `${label} ${t("Common.Form.Invalid")}`,
                ...validationRules
            }}
            render={({ field, fieldState }) => (
                <>
                    <FloatLabel>
                        <Password
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            onBlur={field.onBlur}
                            feedback={true}
                            weakLabel={getStrengthLabel(field.value ?? "")}
                            mediumLabel={getStrengthLabel(field.value ?? "")}
                            strongLabel={getStrengthLabel(field.value ?? "")}
                            strongRegex="^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_])[A-Za-z\d\W_]{6,}$"
                            toggleMask
                            inputId={name as string} // Ensure proper id usage
                            className={fieldState.invalid ? 'p-invalid' : ''} // Use class for error styling
                        />
                        <label htmlFor={name as string}>{label}</label>
                    </FloatLabel>
                    {fieldState.invalid && (
                        <small className="p-error">
                            {fieldState.error?.message}
                        </small>
                    )}
                </>
            )}
        />
    );
};